import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

import { api, formatApiError } from "@/lib/api";
import ReportPageShell from "@/components/reports/ReportPageShell";
import DateRangePicker, { rangeToParams } from "@/components/reports/DateRangePicker";
import ExportDropdown from "@/components/reports/ExportDropdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHART_COLOR = "hsl(var(--primary))";
const GROUPS = [
  { value: "engineer", label: "By engineer" },
  { value: "customer", label: "By customer" },
  { value: "day", label: "By day" },
  { value: "week", label: "By week" },
];

export default function TimeOnSiteReport() {
  const [range, setRange] = useState({ preset: "30d", from: null, to: null });
  const [groupBy, setGroupBy] = useState("engineer");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const my = ++reqIdRef.current;
    setLoading(true);
    try {
      const { data } = await api.get("/reports/time-on-site", {
        params: { ...rangeToParams(range), group_by: groupBy },
      });
      if (my !== reqIdRef.current) return;
      setItems((data.buckets || []).map((b) => ({ ...b, hours: +(b.total_seconds / 3600).toFixed(2) })));
    } catch (err) {
      if (my !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (my === reqIdRef.current) setLoading(false);
    }
  }, [range, groupBy]);

  useEffect(() => { load(); }, [load]);

  const isTime = groupBy === "day" || groupBy === "week";
  const chart = (
    <div style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer>
        {isTime ? (
          <LineChart data={items} margin={{ left: 10, right: 20, top: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} unit="h" />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
            <Line type="monotone" dataKey="hours" stroke={CHART_COLOR} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        ) : (
          <BarChart data={items} layout="vertical" margin={{ left: 60, right: 20, top: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
            <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} unit="h" />
            <YAxis type="category" dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} width={140} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
            <Bar dataKey="hours" fill={CHART_COLOR} radius={[0, 3, 3, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );

  return (
    <ReportPageShell
      testId="tos-report"
      title="Time on Site"
      subtitle="Aggregate service hours across engineers, customers, or dates."
      headerActions={
        <ExportDropdown
          basePath="/reports/time-on-site"
          params={{ ...rangeToParams(range), group_by: groupBy }}
          filename="time-on-site"
          testId="tos-report-export"
        />
      }
      filters={
        <>
          <DateRangePicker value={range} onChange={setRange} testId="tos-report-range" />
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="tos-report-group-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GROUPS.map((g) => (<SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </>
      }
      chartTitle={`Time on site — ${GROUPS.find((g) => g.value === groupBy)?.label.toLowerCase()}`}
      chart={chart}
      table={
        <table className="w-full text-sm" data-testid="tos-report-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 px-3">{GROUPS.find((g) => g.value === groupBy)?.label.replace("By ", "")}</th>
              <th className="text-right py-2 px-3">Total hours</th>
              <th className="text-right py-2 px-3">Jobs</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={3} className="py-8 text-center text-xs text-muted-foreground">No data in range.</td></tr>
            ) : items.map((b) => (
              <tr key={b.key} data-testid={`tos-report-row-${b.key}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="py-2 px-3">{b.label}</td>
                <td className="py-2 px-3 text-right tabular-nums">{b.hours}</td>
                <td className="py-2 px-3 text-right tabular-nums">{b.job_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    />
  );
}
